/**
 * AddPriceRow — Inline input row for adding a new Item Price.
 *
 * Renders inside the PriceListTab footer zone with the same column
 * alignment as data rows. Uses LinkFieldSearch for the Price List
 * dropdown and plain <input> elements (not the UI <Input> which is h-14).
 */

import { useState, useRef, useCallback } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import { useCreateItemPrice } from "../../api/useCreateItemPrice";
import type { ItemPriceEntry } from "../../api/pricingSummaryHooks";
import { SubTableRow } from "./SubTableRow";
import { useTableStyles, SUB_TABLE_DATA_ROW_CLASS } from "./useTableStyles";

interface AddPriceRowProps {
  itemCode: string;
  columnWidths: number[];
  /** Field position indices (mapped to parent column grid) */
  positions: {
    priceList: number;
    rate: number;
    currency: number;
  };
  existingPrices: ItemPriceEntry[];
  onSaved: () => void;
  onCancel: () => void;
}

export function AddPriceRow({
  itemCode,
  columnWidths,
  positions,
  existingPrices,
  onSaved,
  onCancel,
}: AddPriceRowProps) {
  const [priceList, setPriceList] = useState<string | null>(null);
  const [rate, setRate] = useState("");
  const [currency, setCurrency] = useState("THB");
  const [error, setError] = useState<string | null>(null);

  const rateRef = useRef<HTMLInputElement>(null);
  const currencyRef = useRef<HTMLInputElement>(null);
  const { dataCellClass, dataMinHeight } = useTableStyles();

  const mutation = useCreateItemPrice({
    navigateOnSuccess: false,
    itemCode,
    onSuccess: () => {
      toast.success(`Price added to ${priceList}`);
      onSaved();
    },
  });

  const canSave = !!priceList && !!rate && !mutation.isPending;

  const handleSave = useCallback(() => {
    if (!priceList || !rate) return;

    // Client-side duplicate check
    const isDuplicate = existingPrices.some(
      (p) => p.price_list === priceList && p.currency === currency,
    );
    if (isDuplicate) {
      setError("Price already exists for this price list");
      return;
    }
    setError(null);

    mutation.mutate(
      {
        item_code: itemCode,
        price_list: priceList,
        price_list_rate: parseFloat(rate),
        currency,
      },
      {
        onError: (err) => {
          toast.error(err.message || "Failed to add price");
        },
      },
    );
  }, [priceList, rate, currency, itemCode, existingPrices, mutation]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && canSave) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      onCancel();
    }
  };

  const inputClass = cn(
    "h-full w-full bg-transparent border border-neutral-300 dark:border-neutral-600",
    "rounded-xs px-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500",
    "disabled:opacity-50",
  );

  const pos = positions;

  // Build cells for the SubTableRow
  const cells: Record<number, React.ReactNode> = {
    [pos.priceList]: (
      <LinkFieldSearch
        doctype="Price List"
        value={priceList}
        onChange={(v) => {
          setPriceList(v);
          setError(null);
          // After selecting price list, focus rate input
          setTimeout(() => rateRef.current?.focus(), 50);
        }}
        placeholder="Select price list..."
        className="w-full"
      />
    ),
    [pos.rate]: (
      <input
        ref={rateRef}
        type="number"
        step="0.01"
        value={rate}
        onChange={(e) => setRate(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Tab" && !e.shiftKey) {
            // Move to currency field
            e.preventDefault();
            currencyRef.current?.focus();
          }
        }}
        placeholder="0.00"
        disabled={mutation.isPending}
        className={cn(inputClass, "text-right tabular-nums")}
        style={{ maxHeight: dataMinHeight - 8 }}
      />
    ),
    [pos.currency]: (
      <input
        ref={currencyRef}
        type="text"
        value={currency}
        onChange={(e) => setCurrency(e.target.value.toUpperCase())}
        disabled={mutation.isPending}
        className={inputClass}
        style={{ maxHeight: dataMinHeight - 8 }}
      />
    ),
  };

  // Find a column after currency for the action buttons
  const actionCol = Math.min(pos.currency + 1, columnWidths.length - 1);
  if (actionCol !== pos.priceList && actionCol !== pos.rate && actionCol !== pos.currency) {
    cells[actionCol] = (
      <span className="flex items-center gap-1">
        <button
          onClick={handleSave}
          disabled={!canSave}
          className={cn(
            "p-0.5 rounded-md",
            canSave
              ? "text-green-600 hover:text-green-700 dark:text-green-400"
              : "text-muted-foreground opacity-50",
          )}
          aria-label="Save price"
        >
          {mutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
        </button>
        <button
          onClick={onCancel}
          disabled={mutation.isPending}
          className="p-0.5 rounded-md text-muted-foreground hover:text-foreground"
          aria-label="Cancel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  return (
    <div
      data-inline-form-open
      role="form"
      aria-label="Add new price"
      onKeyDown={handleKeyDown}
    >
      <SubTableRow
        columnWidths={columnWidths}
        cellClass={dataCellClass}
        minHeight={dataMinHeight}
        className={cn(
          SUB_TABLE_DATA_ROW_CLASS,
          "bg-blue-50/30 dark:bg-blue-950/20",
        )}
        cells={cells}
      />
      {error && (
        <div className="px-3 py-1 text-xs text-destructive">{error}</div>
      )}
    </div>
  );
}
